import {
  KomboConnectCancelledError,
  showKomboConnect
} from '@kombo-api/connect';
import { useStackOneHub } from '@stackone/react-hub';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { organizationAtom } from 'services/organization/atom';
import { useQuery } from 'services/api/hooks';
import { Flags } from 'services/flags/constants';
import { useFlag } from 'services/flags/hooks';
import {
  createKomboConnectionLink,
  getInterviewStages,
  getIntegrationCredentialsByOrganization,
  getRejectedReasons,
  retrieveConnectSessionToken,
  updateAtsIntegrationActiveState
} from './api';
import {
  integrationAtom,
  integrationResolvedOrganizationAtom,
  interviewStagesAtom,
  rejectedReasonsAtom
} from './atom';
import { IntegrationCredentials } from './types';
import {
  formatRejectedReasons,
  isListsAvailable,
  isRejectionEnabled
} from './utils';

/**
 * Discards the ATS answer currently in state, so the next reader resolves it
 * again.
 *
 * Clears both atoms, and must be the only way either is cleared. They hold one
 * fact — which organization's ATS answer is in state — split in two, and clearing
 * only the credential leaves "resolved for organization X" pointing at nothing.
 * `useResolvedIntegration` cannot defend against that: a genuinely
 * ATS-less organization is *also* a resolved marker with a null credential, so
 * the two states are indistinguishable from there. Which is why the bug it caused
 * was silent — return to an ATS organization before the one you switched to had
 * resolved, and its gate opened on a null credential and rendered the non-ATS
 * flows for good.
 */
export const useClearIntegration = () => {
  const setIntegration = useSetRecoilState(integrationAtom);
  const setResolvedOrganizationId = useSetRecoilState(
    integrationResolvedOrganizationAtom
  );

  return useCallback(() => {
    setIntegration(null);
    setResolvedOrganizationId(null);
  }, [setIntegration, setResolvedOrganizationId]);
};

/**
 * How many times the integration lookup is attempted before the ATS/non-ATS gate
 * gives up and falls through to the no-integration default, and how long it waits
 * between attempts.
 *
 * Exported for the tests, which drive the retries on fake timers.
 */
export const MAX_LOOKUP_ATTEMPTS = 3;
export const LOOKUP_RETRY_DELAY_MS = 2_000;

/**
 * Resolves the org's ATS integration and reports whether the answer is in yet.
 *
 * Use this — not `useIntegration().isConnected` on its own — anywhere the ATS
 * and non-ATS experiences diverge. `isConnected` is derived from a Recoil atom
 * that starts `null`, so until the lookup lands it reads as "no ATS", and a
 * component that branches immediately renders the non-ATS path and then swaps
 * it out mid-interaction once the real answer arrives.
 *
 * It performs the lookup itself rather than relying on `AtsConnector` in the
 * nav bar to have populated the atom as a side effect: that component is
 * conditionally mounted, so callers cannot assume it ran. The
 * `preventInitialFetch` guard keeps this to a single request per organization
 * once the answer is cached.
 *
 * Success is tracked per organization, not as a bare flag: switching
 * organization is a soft switch with no reload, so a flag would stay true, the
 * refetch would stay suppressed, and callers would keep branching on the
 * previous organization's ATS.
 *
 * A failure is retried up to `MAX_LOOKUP_ATTEMPTS` and only then falls through to
 * the no-integration default, so the gate can neither hang on a spinner nor
 * strand an ATS org on the non-ATS experience because one request happened to
 * reject. Failures are held in local state and never in the shared atom: the atom
 * outlives every component, so a cached failure would let `preventInitialFetch`
 * suppress all later attempts for the rest of the session.
 *
 * Responses are matched against the organization the UI is on *now* before being
 * applied. A switch does not cancel the previous organization's request, so
 * without that check an A → B switch where A resolves last would write A's
 * credentials and mark A resolved — leaving B on the gate with no dependency
 * change left to trigger another request, and the previous org's ATS in state.
 */
export const useResolvedIntegration = () => {
  const [resolvedOrganizationId, setResolvedOrganizationId] = useRecoilState(
    integrationResolvedOrganizationAtom
  );
  const [failure, setFailure] = useState<{
    organizationId: string;
    attempts: number;
  } | null>(null);
  const setIntegration = useSetRecoilState(integrationAtom);
  const organization = useRecoilValue(organizationAtom);
  const organizationId = organization?.id;

  // Lets a response callback — which closed over the organization its own request
  // was made for — tell whether that organization is still the current one.
  const currentOrganizationIdRef = useRef(organizationId);
  currentOrganizationIdRef.current = organizationId;

  const hasAnswer =
    !!organizationId && resolvedOrganizationId === organizationId;
  // Both are organization-scoped, so a switch neither inherits the previous
  // organization's failure nor its attempt count.
  const failedForOrganization =
    !!organizationId && failure?.organizationId === organizationId;
  const isRetrying =
    failedForOrganization && (failure?.attempts ?? 0) < MAX_LOOKUP_ATTEMPTS;
  const gaveUp =
    failedForOrganization && (failure?.attempts ?? 0) >= MAX_LOOKUP_ATTEMPTS;

  // Deliberately still false while a retry is pending: opening the gate between
  // attempts would show the non-ATS experience and then snatch it back, which is
  // the flicker this hook exists to prevent.
  const isIntegrationResolved = hasAnswer || gaveUp;

  const { refetch } = useQuery<IntegrationCredentials>(
    getIntegrationCredentialsByOrganization,
    { organizationId },
    {
      preventInitialFetch: isIntegrationResolved || isRetrying,
      onFetchSuccess: (data) => {
        if (organizationId !== currentOrganizationIdRef.current) return;
        setIntegration(data);
        setResolvedOrganizationId(organizationId ?? null);
        setFailure(null);
      },
      onError: () => {
        if (!organizationId) return;
        if (organizationId !== currentOrganizationIdRef.current) return;
        setFailure((previous) => ({
          organizationId,
          attempts:
            (previous?.organizationId === organizationId
              ? previous.attempts
              : 0) + 1
        }));
      }
    }
  );

  // Retries are driven from here rather than by flipping `preventInitialFetch`:
  // neither the params nor that flag change between attempts, so useQuery's own
  // effect would not re-run.
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  useEffect(() => {
    if (!isRetrying) return;

    const timer = setTimeout(() => {
      void refetchRef.current?.();
    }, LOOKUP_RETRY_DELAY_MS);

    return () => clearTimeout(timer);
  }, [isRetrying, failure?.attempts]);

  return { isIntegrationResolved };
};

export const useIntegration = () => {
  const { startConnect } = useStackOneHub();
  const [isLoading, setIsLoading] = useState(false);
  const [integration, setIntegration] = useRecoilState(integrationAtom);
  const isKomboConnectEnabled = useFlag(Flags.USE_KOMBO_CONNECT);

  const startConnectionFlow = useCallback(async () => {
    setIsLoading(true);
    try {
      if (isKomboConnectEnabled) {
        // Kombo path — fetch a Connect URL from the backend, open it in
        // Kombo's hosted iframe, and let the resulting `integration-created`
        // webhook persist the credential. We don't use the resolved
        // activation token here — the credential row is the source of truth
        // and arrives via the existing AppSync subscription.
        const { link } = await createKomboConnectionLink();
        // Clear the loading state before opening the modal so the button
        // doesn't stay disabled for however long the user spends in the
        // Kombo form. Matches the StackOne path which only awaits the
        // session-token fetch, not the iframe lifetime.
        setIsLoading(false);
        try {
          await showKomboConnect(link);
        } catch (error) {
          if (error instanceof KomboConnectCancelledError) {
            return;
          }
          throw error;
        }
        return;
      }

      const { token } = await retrieveConnectSessionToken();
      startConnect({ sessionToken: token });
    } finally {
      setIsLoading(false);
    }
  }, [startConnect, isKomboConnectEnabled]);

  const updateActiveState = useCallback(
    async (isActive: boolean) => {
      if (!integration?.id) return;
      setIsLoading(true);
      const updatedIntegration = await updateAtsIntegrationActiveState({
        id: integration.id,
        isActive
      });
      setIntegration(updatedIntegration);
      setIsLoading(false);
    },
    [integration, setIntegration]
  );

  const disconnect = useCallback(async () => {
    await updateActiveState(false);
  }, [updateActiveState]);

  const reconnect = useCallback(async () => {
    await updateActiveState(true);
  }, [updateActiveState]);

  const reconnectScopes = useCallback(async () => {
    await startConnectionFlow();
  }, [startConnectionFlow]);

  return {
    startConnectionFlow,
    isLoading,
    isConnected: !!integration?.isActive,
    disconnect,
    reconnectScopes,
    reconnect,
    isListsAvailable: isListsAvailable({
      provider: integration?.provider || '',
      isConnected: !!integration?.isActive
    })
  };
};

export const useRejectedReasons = ({
  atsJobId
}: {
  atsJobId: string | undefined | null;
}) => {
  const [rejectedReasons] = useRecoilState(rejectedReasonsAtom);
  const integration = useRecoilValue(integrationAtom);
  const isGreenhouseFeatureFlagEnabled = true;

  return {
    isEnabled: isRejectionEnabled(
      integration,
      atsJobId,
      isGreenhouseFeatureFlagEnabled
    ),
    rejectedReasons
  };
};

export const useInterviewStages = ({
  atsJobId
}: {
  atsJobId?: string | undefined | null;
}) => {
  const [interviewStages] = useRecoilState(interviewStagesAtom);

  return {
    interviewStages,
    isEnabled: !!atsJobId
  };
};

export const useRejectedReasonsFetcher = (
  atsJobId: string | null | undefined
) => {
  const [rejectedReasons, setRejectedReasons] =
    useRecoilState(rejectedReasonsAtom);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(undefined);
  const integration = useRecoilValue(integrationAtom);

  useEffect(() => {
    const fetch = async () => {
      try {
        if (!!rejectedReasons.length || !integration?.id) {
          return;
        }
        setIsLoading(true);
        const data = await getRejectedReasons();
        setRejectedReasons(formatRejectedReasons(data, integration?.provider));
        setError(undefined);
      } catch (error: any) {
        setError(error?.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetch();
  }, [
    atsJobId,
    integration?.id,
    integration?.provider,
    rejectedReasons.length,
    setRejectedReasons
  ]);

  return {
    rejectedReasons,
    isLoading,
    error
  };
};

export const useInterviewStagesFetcher = (
  atsJobId?: string | null | undefined
) => {
  const [interviewStages, setInterviewStages] =
    useRecoilState(interviewStagesAtom);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(undefined);
  const [isUnsupportedProvider, setIsUnsupportedProvider] = useState(false);
  const integration = useRecoilValue(integrationAtom);
  const provider = integration?.provider;

  useEffect(() => {
    const fetch = async () => {
      try {
        // interview stages can be different from one job to another depending on the provider
        // this is why we need to fetch even if array is not empty
        if (!integration?.id) {
          return;
        }
        setIsLoading(true);
        const data = await getInterviewStages({
          atsJobId
        });
        setInterviewStages(data);
        setError(undefined);
        setIsUnsupportedProvider(false);
      } catch (error: any) {
        setError(error?.message);

        if (error.response.data === 'Unsupported provider') {
          setIsUnsupportedProvider(true);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetch();
  }, [atsJobId, integration?.id, provider]);

  return {
    interviewStages,
    isLoading,
    isUnsupportedProvider,
    error
  };
};
